// Helpers de Tienda Nube reusables desde el front:
//  - ensureTnProducts: trae la lista de productos de la tienda de una marca;
//    si la marca no está conectada, abre el popup de OAuth y reintenta.
//  - fireTnImport: dispara el webhook de n8n que crea los productos elegidos.
// El access_token nunca pasa por acá (lo manejan las edge functions server-side).

import { supabase } from "./supabase";

const IMPORT_WEBHOOK_URL =
  "https://ai-agency-dt7d.onrender.com/webhook/tiendanube-import";

export interface TnProduct {
  id: number;
  nombre: string;
  /** Imagen principal (para la miniatura del grid). */
  img_src: string;
  /** Hasta 3 imágenes del producto (para importar). */
  imgs: string[];
}

interface TnProductsResponse {
  connected: boolean;
  products?: TnProduct[];
  store_name?: string | null;
  /** true si la empresa YA tiene otra tienda conectada (para avisar al conectar una 2ª). */
  company_has_stores?: boolean;
}

async function fetchTnProducts(
  brandId: string | null,
): Promise<TnProductsResponse> {
  const { data, error } = await supabase.functions.invoke("tn-products", {
    body: { brand_id: brandId },
  });
  if (error)
    throw new Error(error.message || "No se pudo traer la lista de productos.");
  return data as TnProductsResponse;
}

/** Trae los productos de la tienda; si no está conectada, abre el popup de
 *  OAuth y reintenta. Devuelve los productos + el nombre de la tienda. */
export async function ensureTnProducts(
  brandId: string | null,
  opts?: { confirmNewStore?: () => Promise<boolean> },
): Promise<{ products: TnProduct[]; storeName: string | null }> {
  let res = await fetchTnProducts(brandId);

  if (!res.connected) {
    // Si la empresa YA tiene otra tienda conectada (en otra marca), avisamos
    // antes de conectar una nueva: la que se conecta = la que el comerciante
    // tenga logueada en Tiendanube (no la podemos elegir nosotros — ver ADR-093).
    if (res.company_has_stores && opts?.confirmNewStore) {
      const ok = await opts.confirmNewStore();
      if (!ok) throw new Error("CANCELLED");
    }
    await connectTn(brandId);
    res = await fetchTnProducts(brandId);
  }

  if (!res.connected) throw new Error("No se pudo conectar la tienda.");
  return { products: res.products || [], storeName: res.store_name || null };
}

/** Inicia el OAuth de Tiendanube para una marca (abre el popup y espera la
 *  confirmación). NO trae productos — sirve para el botón "Conectar" de Mi marca. */
export async function connectTn(brandId: string | null): Promise<void> {
  const { data, error } = await supabase.functions.invoke("tn-connect", {
    body: { brand_id: brandId },
  });
  const authorizeUrl = (data as { authorizeUrl?: string } | null)?.authorizeUrl;
  if (error || !authorizeUrl) {
    throw new Error(
      (error as Error | null)?.message || "No se pudo iniciar la conexión.",
    );
  }
  const popup = window.open(authorizeUrl, "tn_connect", "width=620,height=740");
  if (!popup)
    throw new Error("Permití las ventanas emergentes para conectar tu tienda.");
  await waitForConnect(popup);
}

/** Dispara la importación en n8n (corre en background). Tolera que CORS
 *  bloquee la lectura de la respuesta: el request igual llega. */
export async function fireTnImport(params: {
  userId: string | undefined;
  companyId: string | null;
  brandId: string | null;
  productos: { nombre: string; imgs: string[]; tn_product_id: number }[];
}): Promise<void> {
  try {
    await fetch(IMPORT_WEBHOOK_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        user_id: params.userId,
        id_company: params.companyId,
        brand_id: params.brandId,
        productos: params.productos,
      }),
    });
  } catch {
    /* dispara igual aunque CORS bloquee la lectura */
  }
}

/** Estado de conexión de Tienda Nube de una marca (liviano: NO trae productos). */
export async function getTnStatus(
  brandId: string | null,
): Promise<{
  connected: boolean;
  storeName: string | null;
  companyHasStores: boolean;
}> {
  const { data, error } = await supabase.functions.invoke("tn-products", {
    body: { brand_id: brandId, statusOnly: true },
  });
  if (error)
    return { connected: false, storeName: null, companyHasStores: false };
  const d = data as {
    connected?: boolean;
    store_name?: string | null;
    company_has_stores?: boolean;
  };
  return {
    connected: !!d?.connected,
    storeName: d?.store_name || null,
    companyHasStores: !!d?.company_has_stores,
  };
}

/** Desconecta la Tienda Nube de una marca (borra la integración). */
export async function disconnectTn(brandId: string | null): Promise<void> {
  const { error } = await supabase.functions.invoke("tn-disconnect", {
    body: { brand_id: brandId },
  });
  if (error) throw new Error(error.message || "No se pudo desconectar.");
}

// Resuelve cuando el popup confirma ('tn-connected' desde /tn-connected.html,
// mismo origen); rechaza si lo cierran antes o si se agota el tiempo.
function waitForConnect(popup: Window): Promise<void> {
  return new Promise((resolve, reject) => {
    let done = false;

    const cleanup = () => {
      done = true;
      window.removeEventListener("message", onMessage);
      clearInterval(poll);
      clearTimeout(timeout);
      try {
        popup.close();
      } catch {
        /* noop */
      }
    };

    const onMessage = (e: MessageEvent) => {
      const d = e.data as { type?: string; error?: string } | null;
      if (
        d?.type === "tn-connected" &&
        (e.source === popup || e.origin === window.location.origin)
      ) {
        cleanup();
        if (d.error) reject(new Error(d.error));
        else resolve();
      }
    };

    const poll = setInterval(() => {
      if (popup.closed && !done) {
        cleanup();
        reject(new Error("Conexión cancelada."));
      }
    }, 600);

    const timeout = setTimeout(() => {
      if (!done) {
        cleanup();
        reject(new Error("Se agotó el tiempo. Probá de nuevo."));
      }
    }, 180000);

    window.addEventListener("message", onMessage);
  });
}
